/**
 * COMO FUNCIONA SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con Consecuencias y Solución:
 * - Fondo: #ffffff
 * - Colores: #0b1220, #6b7280, #0b63c6
 * - Vertical flow simple con línea conectora
 * - Sin gradientes decorativos
 * - Animaciones reveal con Intersection Observer
 * - Color progresivo en los pasos (opacity)
 */

import React, { useRef, useEffect } from 'react';
import { 
  Video, 
  Settings, 
  TrendingUp,
  CheckCircle2,
  Calendar,
  Clock,
  Users,
  FileText,
  Zap,
  ArrowRight,
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── StepCard Component ───────────────────────────────────────────────────────
const StepCard = ({ paso, index, isLast, delay }) => {
  const ref = useReveal(delay);
  
  // Opacidades progresivas para sensación de flow
  const opacities = ['0.4', '0.7', '1'];
  const opacity = opacities[index] || '1';

  return (
    <div ref={ref} className="relative flex gap-6">
      {/* Número + Línea conectora */}
      <div className="flex flex-col items-center shrink-0">
        {/* Número */}
        <div 
          className="w-14 h-14 rounded-full flex items-center justify-center border-2 z-10"
          style={{ 
            borderColor: `rgba(11, 99, 198, ${opacity})`,
            backgroundColor: index === 2 ? '#0b63c6' : 'white'
          }}
        >
          <span 
            className="text-[18px] font-bold"
            style={{ color: index === 2 ? 'white' : '#0b63c6' }}
          >
            {paso.numero}
          </span>
        </div>

        {/* Línea conectora */}
        {!isLast && (
          <div 
            className="w-0.5 flex-1 mt-2"
            style={{ backgroundColor: `rgba(11, 99, 198, ${opacity})` }}
          />
        )}
      </div>

      {/* Card del paso */}
      <div className="flex-1 pb-8">
        <div className="bg-white rounded-lg border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_rgba(11,99,198,0.08)] hover:border-[#0b63c6]/40 transition-all duration-300 overflow-hidden">
          
          {/* Header */}
          <div className="flex items-start gap-4 p-5 border-b border-black/[0.06]">
            {/* Icon */}
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `rgba(11, 99, 198, ${parseFloat(opacity) * 0.15})` }}
            >
              {React.cloneElement(paso.icon, { 
                size: 24, 
                strokeWidth: 1.75,
                style: { color: '#0b63c6' }
              })}
            </div>

            {/* Título + Duración */}
            <div className="flex-1">
              <h3 className="text-[17px] font-semibold text-[#0b1220] leading-tight mb-1.5">
                {paso.titulo}
              </h3>
              <div className="flex items-center gap-1.5">
                <Clock size={14} strokeWidth={2} className="text-[#6b7280]" />
                <span className="text-[13px] font-medium text-[#6b7280]">
                  {paso.duracion}
                </span>
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div className="p-5 space-y-4">
            <p className="text-[14px] text-[#6b7280] leading-[1.65]">
              {paso.descripcion}
            </p>

            {/* Detalles */}
            <div className="bg-[#f7f9fb] rounded-lg p-4">
              <h4 className="text-[12px] font-semibold text-[#0b1220] uppercase tracking-wide mb-3">
                ¿Qué incluye?
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {paso.detalles.map((detalle, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle2 size={14} strokeWidth={2} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-[13px] text-[#6b7280] leading-snug">
                      {detalle}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Qué pasa después */}
            <div className="pt-3 border-t border-black/[0.06]">
              <div className="flex items-start gap-2">
                <ArrowRight size={16} strokeWidth={2} className="text-[#0b63c6] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[13px] font-semibold text-[#0b1220]">
                    Siguiente paso:{' '}
                  </span>
                  <span className="text-[13px] text-[#6b7280]">
                    {paso.que_pasa}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ComoFunciona = () => {
  const headerRef = useReveal(0);
  const featuresRef = useReveal(0);
  const ctaRef = useReveal(0);

  const scrollToForm = () => {
    document.getElementById('formulario-contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  const pasos = [
    {
      numero: "01",
      icon: <Video />,
      titulo: "Diagnóstico Gratuito",
      duracion: "15 minutos",
      descripcion: "Agendas una videollamada con nosotros. Conversamos sobre tu operación actual: cuántos vehículos tienes, qué rutas haces, cómo llevas el control hoy.",
      detalles: [
        "Sin compromiso",
        "100% virtual",
        "Te mostramos el sistema en vivo",
        "Resolvemos todas tus dudas"
      ],
      que_pasa: "Recibes una propuesta personalizada con el alcance y la inversión."
    },
    {
      numero: "02",
      icon: <Settings />,
      titulo: "Configuración Personalizada",
      duracion: "3-7 días",
      descripcion: "Configuramos el sistema con tus datos: vehículos, choferes, rutas principales, clientes. No tienes que hacer nada técnico, nosotros nos encargamos.",
      detalles: [
        "Cargamos tus vehículos y choferes",
        "Configuramos tus rutas frecuentes",
        "Adaptamos a tus procesos",
        "Te capacitamos en 1 hora"
      ],
      que_pasa: "Tienes una sesión de capacitación de 1 hora donde te enseñamos a usar el sistema."
    },
    {
      numero: "03",
      icon: <TrendingUp />,
      titulo: "Empiezas a Ver Resultados",
      duracion: "Desde el día 1",
      descripcion: "Registras tu primer viaje y al instante ves si fue rentable o no. Cada día tienes más claridad sobre dónde ganas y dónde pierdes.",
      detalles: [
        "Rentabilidad por viaje en tiempo real",
        "Alertas automáticas de pérdidas",
        "Reportes cuando los necesites",
        "Soporte continuo"
      ],
      que_pasa: "En las primeras semanas, ya identificas viajes no rentables y recuperas la inversión."
    }
  ];

  const features = [
    {
      icon: <Users />,
      titulo: "Acompañamiento personal",
      descripcion: "No te dejamos solo. Tienes soporte directo por WhatsApp y videollamadas cuando lo necesites."
    },
    {
      icon: <FileText />,
      titulo: "Sin contratos largos",
      descripcion: "Planes mensuales sin permanencia. Si no te sirve, puedes cancelar cuando quieras."
    },
    {
      icon: <Zap />,
      titulo: "Implementación rápida",
      descripcion: "En menos de una semana estás operativo. No necesitas parar tu operación ni contratar personal."
    }
  ];

  return (
    <section
      id="como-funciona"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header ref={headerRef} className="max-w-[720px] flex flex-col gap-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 w-fit">
            <Zap size={14} strokeWidth={2} className="text-[#0b63c6]" />
            <span className="text-[13px] font-medium text-[#0b63c6]">Proceso simple</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            Empieza a controlar tu rentabilidad en{' '}
            <span className="text-[#0b63c6]">3 pasos simples</span>
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            No necesitas ser experto en sistemas ni invertir semanas de tiempo.{' '}
            <strong className="text-[#0b1220] font-semibold">Te acompañamos en todo el proceso</strong>{' '}
            desde el primer contacto hasta que veas resultados.
          </p>
        </header>

        {/* ── Pasos con timeline vertical ── */}
        <div className="max-w-[800px]">
          {pasos.map((paso, index) => (
            <StepCard
              key={index}
              paso={paso}
              index={index}
              isLast={index === pasos.length - 1}
              delay={index * 80}
            />
          ))}
        </div>

        {/* ── Features en grid horizontal ── */}
        <div
          ref={featuresRef}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6 rounded-xl bg-[#f7f9fb] border border-black/[0.08]"
        >
          {features.map((feature, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center">
                {React.cloneElement(feature.icon, { 
                  size: 20, 
                  strokeWidth: 1.75, 
                  className: 'text-[#0b63c6]' 
                })}
              </div>
              <div>
                <h4 className="text-[14px] font-semibold text-[#0b1220] mb-1">
                  {feature.titulo}
                </h4>
                <p className="text-[13px] text-[#6b7280] leading-[1.6]">
                  {feature.descripcion}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── CTA Final ── */}
        <div
          ref={ctaRef}
          className="flex flex-col lg:flex-row items-center justify-between gap-6 p-8 rounded-xl bg-gradient-to-r from-[#0b1220] to-[#1e293b] border border-white/10"
        >
          {/* Content */}
          <div className="flex flex-col gap-2 flex-1">
            <h3 className="text-[20px] font-bold text-white leading-tight">
              ¿Listo para tener control total de tu rentabilidad?
            </h3>
            <p className="text-[14px] text-white/80 leading-[1.6]">
              Agenda tu diagnóstico gratuito de 15 minutos y descubre cuánto podrías estar recuperando.
            </p>
          </div>

          {/* Button + Note */}
          <div className="flex flex-col items-center lg:items-end gap-3 shrink-0">
            <button
              onClick={scrollToForm}
              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-[#0b1220] text-[14px] font-semibold px-6 py-3 rounded-lg transition-colors duration-200 shadow-lg"
            >
              <Calendar size={18} strokeWidth={2} />
              Agendar Diagnóstico Gratuito
            </button>
            
            <div className="flex items-center gap-2 text-[12px] text-white/70">
              <CheckCircle2 size={14} strokeWidth={2} />
              <span>Sin compromiso • 100% virtual • Respuesta en 24h</span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ComoFunciona;